import React from 'react';
import { ArrowRight, Check, Bell, ArrowUpRight, Zap } from 'lucide-react';

const HeroSection = () => {
  // Using your brand colors with slight enhancements
  const colors = {
    primary: "#122721",     // Dark green font color
    background: "#6cf168",  // Light green background
    accent: "#0f211b",      // Slightly lighter shade for hover effects
    gradient: "linear-gradient(135deg,rgb(247, 247, 247) 0%,rgb(228, 228, 228) 100%)",
  };

  return (
    <div className="w-full py-16 md:py-16 lg:py-24 overflow-hidden" style={{ background: colors.gradient }}>
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-12">
          
          {/* Left content - Text and CTA */}
          <div className="w-full lg:w-2/5 z-10">
            <div className="bg-white/10 inline-block  py-1.5 md:px-4 md:py-2 rounded-full mb-4 backdrop-blur-sm">
              <span className="text-xs md:text-sm font-medium flex items-center" style={{ color: colors.primary }}>
                <Zap size={14} className="mr-1.5 md:mr-2" style={{ color: colors.primary }} />
                UAE's #1 Financial Management Solution
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-4xl md:text-5xl lg:text-5xl font-bold mb-4 md:mb-6 leading-tight" style={{ color: colors.primary }}>
              Simplify Your UAE <br />Business Finances
            </h1>
            
            <p className="text-base md:text-lg lg:text-xl mb-6 md:mb-8 max-w-lg" style={{ color: colors.primary }}>
              Arbaah streamlines your invoicing, bookkeeping, and financial management with powerful features designed specifically for UAE businesses.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-8 md:mb-12">
              <a 
                href="https://app.arbaah.ae/authentication/sign-up" 
                className="group inline-flex items-center justify-center px-6 py-3 md:px-8 md:py-4 text-white font-medium rounded-full transition-all"
                style={{ backgroundColor: colors.primary }}
              >
                <span className="text-sm md:text-base">Start for free</span>
                <div className="ml-2 bg-white rounded-full w-6 h-6 md:w-8 md:h-8 flex items-center justify-center group-hover:bg-opacity-90 transition-all">
                  <ArrowRight size={14} className="md:hidden" stroke={colors.primary} strokeWidth={3} />
                  <ArrowRight size={16} className="hidden md:block" stroke={colors.primary} strokeWidth={3} />
                </div>
              </a>
            </div>
            
            {/* Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div className={`flex items-start p-3 md:p-4 rounded-xl backdrop-blur-sm`} style={{ background: colors.background }}>
                <div className="mr-3 md:mr-4 mt-0.5 md:mt-1 bg-white rounded-full p-1 flex-shrink-0">
                  <Check size={14} className="md:hidden" style={{ color: colors.primary }} />
                  <Check size={16} className="hidden md:block" style={{ color: colors.primary }} />
                </div>
                <div>
                  <p className="text-xs md:text-sm font-medium" style={{ color: colors.primary }}>
                    80% less time creating invoices with Voice-to-Invoice technology
                  </p>
                </div>
              </div>
              
              <div className={`flex items-start p-3 md:p-4 rounded-xl backdrop-blur-sm`} style={{ background: colors.background }}>
                <div className="mr-3 md:mr-4 mt-0.5 md:mt-1 bg-white rounded-full p-1 flex-shrink-0">
                  <Check size={14} className="md:hidden" style={{ color: colors.primary }} />
                  <Check size={16} className="hidden md:block" style={{ color: colors.primary }} />
                </div>
                <div>
                  <p className="text-xs md:text-sm font-medium" style={{ color: colors.primary }}>
                    Prevent fines with automated tax regulation compliance checks
                  </p>
                </div>
              </div>
              
              <div className={`flex items-start p-3 md:p-4 rounded-xl backdrop-blur-sm`} style={{ background: colors.background }}>
                <div className="mr-3 md:mr-4 mt-0.5 md:mt-1 bg-white rounded-full p-1 flex-shrink-0">
                  <Bell size={14} className="md:hidden" style={{ color: colors.primary }} />
                  <Bell size={16} className="hidden md:block" style={{ color: colors.primary }} />
                </div>
                <div>
                  <p className="text-xs md:text-sm font-medium" style={{ color: colors.primary }}>
                    Get notified for new tax laws and expiring business documents
                  </p>
                </div>
              </div>
              
              <div className={`flex items-start p-3 md:p-4 rounded-xl backdrop-blur-sm`} style={{ background: colors.background }}>
                <div className="mr-3 md:mr-4 mt-0.5 md:mt-1 bg-white rounded-full p-1 flex-shrink-0">
                  <Check size={14} className="md:hidden" style={{ color: colors.primary }} />
                  <Check size={16} className="hidden md:block" style={{ color: colors.primary }} />
                </div>
                <div>
                  <p className="text-xs md:text-sm font-medium" style={{ color: colors.primary }}>
                    Instantly scan and digitize invoices with advanced OCR technology
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="w-full lg:w-3/5 relative">
            {/* Main dashboard image with 3D effect */}
            <div className="relative rounded-xl md:rounded-2xl overflow-hidden shadow-xl md:shadow-2xl transform transition-all hover:scale-[1.02] hover:-rotate-1" 
                 style={{ 
                   boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                 }}>
              <img 
                src="/hero.png" 
                alt="Arbaah Bookkeeping Dashboard" 
                className="w-full h-auto object-cover rounded-xl md:rounded-2xl"
              />
              
              {/* Overlay gradient for better contrast with UI elements */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              
              {/* Floating UI Element - Invoice Notification */}
              <div className="absolute top-3 right-3 md:top-6 md:right-6 bg-white py-2 px-3 md:py-3 md:px-5 rounded-lg md:rounded-xl shadow-lg transform transition-all hover:scale-105 animate-pulse">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="bg-green-500 rounded-full w-8 h-8 md:w-10 md:h-10 flex items-center justify-center flex-shrink-0">
                    <Check size={16} className="text-white md:hidden" />
                    <Check size={20} className="text-white hidden md:block" />
                  </div>
                  <div>
                    <p className="text-[10px] md:text-xs text-gray-500 mb-0.5 md:mb-1">Just now</p>
                    <p className="font-semibold text-xs md:text-sm" style={{ color: colors.primary }}>
                      Invoice #AR-2025-4391 Created
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Floating UI Element - Revenue Card */}
              <div className="absolute bottom-16 right-0 md:bottom-32 md:right-4 bg-white py-2 px-3 md:py-4 md:px-6 rounded-lg md:rounded-xl shadow-lg md:shadow-xl transform transition-all hover:translate-x-2 hover:scale-105">
                <div className="flex flex-col">
                  <p className="text-gray-500 text-[10px] md:text-xs mb-0.5 md:mb-1">Monthly Revenue</p>
                  <div className="flex items-center">
                    <span className="text-sm font-bold text-green-500 md:text-2xl">1,063,450 AED</span>
                    <span className="ml-1 md:ml-2 bg-green-100 text-green-600 text-[10px] md:text-xs px-1.5 py-0.5 md:px-2 md:py-1 rounded-full">+12.4%</span>
                  </div>
                </div>
              </div>
              
              {/* Floating UI Element - Voice Feature */}
              <div className="absolute top-1/3 left-0 md:left-4 bg-white py-2 px-3 md:py-3 md:px-5 rounded-lg md:rounded-xl shadow-lg transform transition-all hover:-translate-y-2" 
                   style={{ borderLeft: `3px solid ${colors.primary}`, borderLeftWidth: '3px md:5px' }}>
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="bg-teal-100 rounded-full w-8 h-8 md:w-10 md:h-10 flex items-center justify-center flex-shrink-0">
                    <svg width="16" height="16" className="md:hidden" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                      <line x1="12" y1="19" x2="12" y2="23" />
                      <line x1="8" y1="23" x2="16" y2="23" />
                    </svg>
                    <svg width="20" height="20" className="hidden md:block" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                      <line x1="12" y1="19" x2="12" y2="23" />
                      <line x1="8" y1="23" x2="16" y2="23" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-xs md:text-sm" style={{ color: colors.primary }}>
                      Voice-to-Invoice
                    </p>
                    <p className="text-[10px] md:text-xs text-gray-500">Say it, create it</p>
                  </div>
                </div>
              </div>
              
              {/* Floating UI Element - Tax Compliance */}
              <div className="absolute bottom-6 left-5 md:bottom-12 md:left-10 bg-white py-1.5 px-3 md:py-2 md:px-4 rounded-md md:rounded-lg shadow-lg transform transition-all hover:translate-y-1">
                <div className="flex items-center gap-1.5 md:gap-2">
                  <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-green-500"></div>
                  <p className="text-[10px] md:text-xs font-medium" style={{ color: colors.primary }}>
                    Tax Compliance: All Clear
                  </p>
                </div>
              </div>
            </div>
            
            {/* Background decorative elements */}
            <div className="absolute bottom-0 right-0 w-32 h-32 md:w-64 md:h-64 bg-blue-200 rounded-full opacity-20 blur-3xl"></div>
            <div className="absolute top-0 left-0 w-24 h-24 md:w-48 md:h-48 bg-green-400 rounded-full opacity-20 blur-3xl"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;